import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search, Plus, Eye, ChevronRight } from "lucide-react";
import { API_URL } from "./config";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { useNavigate } from "react-router-dom";

const controlItems = [
  "In Trip Location Sync Interval",
  "New Trip Location Sync Interval",
  "New Driver Free Interval",
  "Last Support Date Previous Version",
  "APK Version Name",
  "Latest Update Available Feature 1",
  "Latest Update Available Feature 2",
  "Latest Update Available Feature 3",
  "Latest APK Download Link (URL)",
  "Feature Availability Request Ride Share",
  "Feature Availability Request Delivery",
  "Feature Availability Request Car Hire",
  "Feature Availability Request Construction Equipment",
  "Feature Availability Request Go Online Shopping",
  "Feature Availability Request Road Assistance",
  "Feature Availability Request More About Safety",
];

const Configurations = () => {
  const [configData, setConfigData] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [modalShow, setModalShow] = useState(false);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [controlItem, setControlItem] = useState(""); // New state for selected control item
  const [featureConfigs, setFeatureConfigs] = useState([]); // New state for feature configurations
  const navigate = useNavigate();

  useEffect(() => {
    fetchConfigData();
    fetchFeatureConfigs();
  }, []);

  const fetchFeatureConfigs = async () => {
    try {
      const response = await fetch(`${API_URL}/application_configs/features`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setFeatureConfigs(data);
    } catch (error) {
      console.error("Failed to fetch feature configurations:", error);
    }
  };

  const toggleFeature = async (controlItem, currentValue) => {
    try {
      const newValue = currentValue === 'yes' ? 'no' : 'yes';
      const response = await fetch(`${API_URL}/application_configs/features`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          control_item: controlItem,
          value: newValue
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      console.log(`Feature ${controlItem} updated to: ${newValue}`);

      // Update local state
      setFeatureConfigs(prev => 
        prev.map(config => 
          config.control_item === controlItem 
            ? { ...config, value: newValue }
            : config
        )
      );
    } catch (error) {
      console.error("Failed to toggle feature:", error);
    }
  };

  const fetchConfigData = async () => {
    try {
      const response = await fetch(`${API_URL}/application_configs`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      
      // Sort data by the timestamp property, assuming it's named `createdAt`
      const sortedData = data.sort((a, b) => new Date(b.date) - new Date(a.date));
      
      setConfigData(sortedData);
      setErrorMessage(null);
    } catch (error) {
      setErrorMessage(
        "Failed to fetch configuration data. Please try again later."
      );
    }
  };
  const filteredData = configData.filter((config) => {
    const matchesSearchTerm =
      config.control_item.toLowerCase().includes(searchTerm.toLowerCase()) ||
      config.value.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesControlItem = controlItem
      ? config.control_item === controlItem
      : true;
    return matchesSearchTerm && matchesControlItem;
  });

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-white p-6">
        <div className="max-w-7xl mx-auto">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4"
          >
            <div className="flex flex-col md:flex-row justify-between items-center mb-6">
              <h1 className="text-3xl font-bold text-[#002966] mb-4 md:mb-0">
                Configurations
              </h1>
              <select
                required
                value={controlItem}
                onChange={(e) => setControlItem(e.target.value)}
                className="flex items-center bg-gradient-to-br from-[#001233] to-[#002966] border border-white/20 rounded-lg px-4 py-3 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffc000]/50 transition-all"
              >
                <option value="" className="bg-[#002966]">
                  Select a control item
                </option>
                {controlItems.map((item) => (
                  <option key={item} value={item} className="bg-[#002966]">
                    {item}
                  </option>
                ))}
              </select>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate("/AddConfig")}
                className="flex items-center mt-4 md:mt-0 px-4 py-2 bg-[#ffc000] hover:bg-[#ffcd2d] text-[#002966] rounded-lg font-semibold shadow-lg transition-all"
              >
                <Plus className="w-5 h-5 mr-2" />
                Add Configuration
              </motion.button>
            </div>

            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search configurations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-gradient-to-br from-[#001233] to-[#002966] border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#ffc000]/50"
              />
            </div>
          </motion.div>

          {/* Error Message */}
          {errorMessage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="bg-red-500/10 border border-red-500/20 text-red-500 p-4 rounded-lg mb-6"
            >
              {errorMessage}
            </motion.div>
          )}
          {/* Configuration Cards */}
          <div className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-3">
            {filteredData.length > 0 ? (
              filteredData.map((config) => (
                <motion.div
                  key={config.application_config_id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02 }}
                  className="w-1/2 max-w-md mx-auto bg-gradient-to-br from-[#001233] to-[#002966] backdrop-blur-lg rounded-lg p-6 border border-white/20 shadow-xl"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-white">
                      {config.control_item}
                    </h3>
                    <span className="px-3 py-1 bg-[#ffc000]/20 text-[#ffc000] rounded-full text-sm">
                      ID: {config.application_config_id}
                    </span>
                  </div>
                  <div className="space-y-2 text-gray-300">
                    <p>
                      <span className="text-[#ffc000]">Value:</span>{" "}
                      {config.value}
                    </p>
                    <p>
                      <span className="text-[#ffc000]">User ID:</span>{" "}
                      {config.user_id}
                    </p>
                    <p>
                      <span className="text-[#ffc000]">Date:</span>{" "}
                      {new Date(config.date).toLocaleDateString()}
                    </p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      setSelectedConfig(config);
                      setModalShow(true);
                    }}
                    className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white transition-all"
                  >
                    <Eye className="w-4 h-4 mr-2" />
                    View Details
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </motion.button>
                </motion.div>
              ))
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="col-span-full text-center text-gray-400 py-12"
              >
                No configuration data available.
              </motion.div>
            )}
          </div>

          {/* Feature Availability Section */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8 p-4"
          >
            <div className="flex flex-col md:flex-row justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#002966] mb-4 md:mb-0">
                Feature Availability Management
              </h2>
            </div>

            {/* Feature Toggle Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {featureConfigs.map((feature) => (
                <motion.div
                  key={feature.control_item}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02 }}
                  className="bg-gradient-to-br from-[#001233] to-[#002966] backdrop-blur-lg rounded-lg p-6 border border-white/20 shadow-xl"
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-lg font-semibold text-white">
                      {feature.control_item.replace('Feature Availability Request ', '')}
                    </h3>
                    <span className={`px-3 py-1 rounded-full text-sm ${
                      feature.value === 'yes' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-red-500 text-white'
                    }`}>
                      {feature.value === 'yes' ? 'Active' : 'Disabled'}
                    </span>
                  </div>
                  <div className="space-y-3 text-gray-300">
                    <p>
                      <span className="text-[#ffc000]">Status:</span>{" "}
                      <span className={feature.value === 'yes' ? 'text-green-400' : 'text-red-400'}>
                        {feature.value === 'yes' ? 'Available to Users' : 'Hidden from Users'}
                      </span>
                    </p>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => toggleFeature(feature.control_item, feature.value)}
                      className={`w-full px-4 py-2 rounded-lg font-semibold transition-all ${
                        feature.value === 'yes'
                          ? 'bg-red-600 hover:bg-red-700 text-white'
                          : 'bg-green-600 hover:bg-green-700 text-white'
                      }`}
                    >
                      {feature.value === 'yes' ? 'Disable Feature' : 'Enable Feature'}
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Modal */}
        {modalShow && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-[#001233] border border-white/20 rounded-lg p-6 max-w-lg w-full mx-4"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-bold text-white">
                  Configuration Details
                </h3>
                <button
                  onClick={() => setModalShow(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ×
                </button>
              </div>
              {selectedConfig && (
                <div className="space-y-4 text-gray-300">
                  <p className="flex justify-between">
                    <span className="text-[#ffc000]">
                      Application Config ID:
                    </span>
                    <span>{selectedConfig.application_config_id}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-[#ffc000]">Control Item:</span>
                    <span>{selectedConfig.control_item}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-[#ffc000]">Value:</span>
                    <span>{selectedConfig.value}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-[#ffc000]">User ID:</span>
                    <span>{selectedConfig.user_id}</span>
                  </p>
                  <p className="flex justify-between">
                    <span className="text-[#ffc000]">Date:</span>
                    <span>
                      {new Date(selectedConfig.date).toLocaleDateString()}
                    </span>
                  </p>
                </div>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setModalShow(false)}
                className="mt-6 w-full px-4 py-2 bg-[#ffc000] hover:bg-[#ffcd2d] text-[#002966] rounded-lg font-semibold"
              >
                Close
              </motion.button>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Configurations;
